import axios from "axios";
import i18n from "../../i18n";

// const API_URL = "https://backend-proxy.yros.io"
// const API_URL = "http://127.0.0.1:5003"
// const API_BASE_URL = API_URL + "/proxy/api";
const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5003";
const API_BASE_URL = API_URL + (process.env.REACT_APP_API_BASE_URL || "/proxy/api");

const defaultHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json",
};

const getSessionExpiredMessage = () => i18n.t('apiService.sessionExpired');
const getNetworkErrorMessage = () => i18n.t('apiService.networkError');

// JWT now travels in HttpOnly cookies set by the proxy at /login. The proxy
// also sets a non-HttpOnly csrf_access_token cookie that we must echo back in
// the X-CSRF-TOKEN header on mutating requests (double-submit pattern).
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

// Staging vit sous un sous-domaine de la prod (app.staging.yros.io), donc les
// cookies posés par la prod sur .yros.io lui arrivent aussi. Le proxy suffixe
// ses cookies par environnement pour lever l'ambiguïté ; on lit le même nom
// ici. Doit valoir le JWT_COOKIE_SUFFIX du proxy, vide en prod.
const COOKIE_SUFFIX = process.env.REACT_APP_COOKIE_SUFFIX || "";

function readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function readCsrfToken() {
    return readCookie(`csrf_access_token${COOKIE_SUFFIX}`);
}

function redirectToForbidden() {
    if (window.location.pathname !== "/403") {
        window.location.href = "/403";
    }
}

// Documents d'un ordre de mission (Yros/Kavero#425, #465) : suffixe du chemin
// sous /rh/ordres-mission/<id> et préfixe du nom de fichier, comme côté Odoo.
const DOCUMENTS_ORDRE_MISSION = {
    ordre: { chemin: "", prefixe: "ordre-mission" },
    engagement: { chemin: "/engagement-retour", prefixe: "engagement-retour" },
};
const TYPES_FORMAT_DOCUMENT = {
    pdf: "application/pdf",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

// `Blob.text()` manque à jsdom : `FileReader` lit un Blob partout.
const lireTexte = (blob) => new Promise((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resolve(lecteur.result);
    lecteur.onerror = () => reject(lecteur.error);
    lecteur.readAsText(blob);
});

// Suffixe d'un nom de fichier tiré d'un nom de personne, réduit à `[a-z0-9-]`
// (même règle que le badge) : le proxy ne relaie pas le Content-Disposition.
function suffixeFichier(nom, repli) {
    const suffixe = String(nom || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return suffixe || String(repli);
}

// Documents d'un départ (Yros/Kavero#477, #61) : chemin sous
// /api/rh/departs/<id> et préfixe du nom de fichier, comme côté Odoo.
const DOCUMENTS_DEPART = {
    certificat: { chemin: "certificat-travail", prefixe: "certificat-travail" },
    recu: { chemin: "recu-stc", prefixe: "recu-stc" },
};

class ApiService {
    constructor() {
        this.refreshTokenPromise = null;

        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            headers: defaultHeaders,
            withCredentials: true,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const method = (config.method || "get").toLowerCase();
                if (MUTATING_METHODS.has(method)) {
                    const csrf = readCsrfToken();
                    if (csrf) {
                        config.headers["X-CSRF-TOKEN"] = csrf;
                    }
                }
                return config;
            },
            (error) => { throw error; }
        );

        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 403) {
                    redirectToForbidden();
                    throw this.enrichError(error);
                }

                if (error.response && error.response.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    try {
                        await this.refreshToken();
                        return this.axiosInstance(originalRequest);
                    } catch (refreshError) {
                        console.error("Session expirée. Déconnexion...");
                        this.logoutUser(getSessionExpiredMessage());
                        throw this.enrichError(refreshError);
                    }
                }   

                if (error.response && error.response.status === 401) {
                    this.logoutUser(getSessionExpiredMessage());
                }

                if (!error.response && this.shouldRetryNetworkError(originalRequest)) {
                    originalRequest._networkRetry = true;
                    await this.delay(500);
                    return this.axiosInstance(originalRequest);
                }

                throw this.enrichError(error);
            }
        );
    }

    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    shouldRetryNetworkError(requestConfig) {
        if (!requestConfig || requestConfig._networkRetry) {
            return false;
        }

        const method = (requestConfig.method || "get").toLowerCase();
        return ["get", "head", "options"].includes(method);
    }

    extractBackendMessage(error) {
        return (
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            null
        );
    }

    enrichError(error) {
        if (!error) {
            return error;
        }

        const backendMessage = this.extractBackendMessage(error);
        const userMessage = backendMessage || (
            error?.message === "Network Error" ? getNetworkErrorMessage() : i18n.t('apiService.genericError')
        );

        error.backendMessage = backendMessage;
        error.userMessage = userMessage;

        return error;
    }

    async refreshToken() {
        // The HttpOnly refresh_token_cookie is sent automatically thanks to
        // withCredentials. CSRF for refresh comes from csrf_refresh_token.
        if (!this.refreshTokenPromise) {
            const csrfRefresh = readCookie(`csrf_refresh_token${COOKIE_SUFFIX}`);
            this.refreshTokenPromise = axios.post(`${API_URL}/refresh`, {}, {
                withCredentials: true,
                headers: csrfRefresh ? { "X-CSRF-TOKEN": csrfRefresh } : {},
            }).finally(() => {
                this.refreshTokenPromise = null;
            });
        }

        await this.refreshTokenPromise;
    }

    logoutUser(message) {
        if (message) {
            sessionStorage.setItem("authMessage", message);
        }
        // Best-effort server-side cookie clearing; ignore failure (offline,
        // network error, expired session): we still want to redirect.
        axios.post(`${API_URL}/logout`, {}, { withCredentials: true }).catch(() => {});
        localStorage.removeItem("companyName");
        localStorage.removeItem('isConnected');
        localStorage.removeItem('user');
        window.location.href = "/login";
    }

    async authenticate(email, password, country) {
        return axios.post(API_URL + "/login", {
            login: email,
            password: password,
            country: country,
        }, {
            headers: defaultHeaders,
            withCredentials: true,
        });
    }

    async register(data) {
        return axios.post(API_URL + "/register", data, {
            headers: defaultHeaders,
            withCredentials: true,
        });
    }

    async getBlobFile(endpoint) {
        return this.axiosInstance.get(endpoint, {
            responseType: "blob",
        });
    }

    async getElement(endpoint) {
        return this.axiosInstance.get(endpoint, { headers: defaultHeaders });
    }

    async getElementById(endpoint, id) {
        return this.axiosInstance.get(`${endpoint}/${id}`, { headers: defaultHeaders });
    }

    async saveElement(endpoint, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.post(endpoint, sendFile ? data : JSON.stringify(data), { headers });
    }

    async searchElement(endpoint, data) {
        return this.axiosInstance.post(endpoint, JSON.stringify(data), { headers: defaultHeaders });
    }

    async searchFile(endpoint, data) {
        return this.axiosInstance.post(endpoint, JSON.stringify(data), {
            headers: defaultHeaders,
            responseType: "blob",
        });
    }

    async updateElementById(endpoint, id, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.put(`${endpoint}/${id}/`, sendFile ? data : JSON.stringify(data), { headers });
    }

    async updateElement(endpoint, data = {}, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.put(endpoint, sendFile ? data : JSON.stringify(data), { headers });
    }

    async changeElementById(endpoint, id, data, sendFile = false) {
        const headers = sendFile
            ? { ...defaultHeaders, "Content-Type": "multipart/form-data" }
            : defaultHeaders;

        return this.axiosInstance.patch(`${endpoint}/${id}/`, sendFile ? data : JSON.stringify(data), { headers });
    }

    async deleteElement(endpoint, id) {
        return this.axiosInstance.delete(`${endpoint}/${id}/`, { headers: defaultHeaders });
    }

    async exportData({ model_name, fields, domain = [], ids = [], format = "csv", filename = "export" }) {
        const response = await this.searchElement('/export', {
            model_name,
            fields,
            domain,
            ids
        });
        const data = response.data.result.data || [];

       if (format === "csv") {
            const csvRows = [];
            csvRows.push(fields.join(';'));
            data.forEach(row => {
                csvRows.push(
                    fields.map(f => {
                        if ((f === "employee_id" || f === "absence_type_id") && row[f][1]) {
                            return row[f][1];
                        }
                        const val = row[f];
                        return (val === false || val === undefined || val === null) ? "" : val;
                    }).join(';')
                );
            });
            const blob = new Blob([csvRows.join('\r\n')], { type: "text/csv" });
            this.downloadBlob(blob, filename + ".csv");
        } else if (format === "xlsx") {
            const XLSX = await import("xlsx");
            const cleanData = data.map(row =>
                Object.fromEntries(
                    fields.map(f => [
                        f,
                        ((f === "employee_id" || f === "absence_type_id" )&& row.employee_id && Array.isArray(row[f]) && row[f][1])
                            ? row[f][1]
                            : ((row[f] === false || row[f] === undefined || row[f] === null) ? "" : row[f])
                    ])
                )
            );
            const ws = XLSX.utils.json_to_sheet(cleanData, { header: fields });
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Export");
            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            this.downloadBlob(blob, filename + ".xlsx");
        } else {
            throw new Error(i18n.t('apiService.unsupportedFormat'));
        }
    }

    downloadBlob(blob, filename) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    }

    async sendEmail({ to, cc, subject, html, template_id, params, attachment }) {
        // Use axios instance so Authorization header is attached by interceptor
        const response = await this.axiosInstance.post(
            `${API_URL}/api/send-email`,
            { to, cc, subject, html, template_id, params, attachment },
            { headers: { "Content-Type": "application/json" } }
        );
        return response.data;
    }

    // Configuration du contrôle de zone du pointage (contrat C13,
    // Yros/Kavero#348). Route DÉDIÉE du proxy, appelée en absolu comme
    // `listerDemandesRh` : le préfixe /proxy/api de axiosInstance mène au
    // tunnel générique vers Odoo, fermé sur cette route. Aucune query
    // string au GET, le proxy la refuse.
    async getLieuTravail(employeId) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/lieu-travail`, {
            params: { employe_id: employeId },
        });
        return response.data;
    }

    async affecterLieuTravail(employeId, lieuId) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/lieu-travail`, {
            employe_id: employeId,
            lieu_id: lieuId,
        });
        return response.data;
    }

    async getConfigurationPointage() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/configuration-pointage`);
        return response.data;
    }

    async enregistrerConfigurationPointage(config) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/configuration-pointage`, config);
        return response.data;
    }

    // Régénère le QR d'un lieu de travail (contrat C25) : les affiches déjà
    // imprimées cessent de fonctionner. Le refus métier sort en 409.
    async regenererQrLieu(lieuId) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/regenerer-qr-lieu`, { lieu_id: lieuId });
        return response.data;
    }

    // Bornes de société de l'acompte, de l'avance et du prêt (Yros/Kavero#411).
    async lireParametresAvances() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/parametres-avances`);
        return response.data;
    }

    async ecrireParametresAvances(parametres) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/parametres-avances`, parametres);
        return response.data;
    }

    // Horaires de travail de la société (contrat C17, Yros/Kavero#16).
    async lireHorairesTravail(horaireId) {
        // Yros/Kavero#491 : un autre horaire de la société.
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/horaires-travail`,
            horaireId ? { params: { horaire_id: horaireId } } : undefined);
        return response.data;
    }

    // Yros/Kavero#491 : les heures sup calculées au pointage, à valider.
    async lireHeuresSupPointage() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/heures-sup-pointage`);
        return response.data;
    }

    async deciderHeuresSupPointage(id, approuver, motif) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/heures-sup-pointage/${id}/decision`,
            motif === undefined ? { approuver } : { approuver, motif });
        return response.data;
    }

    async lireHoraireSalarie(employeId) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/employes/${employeId}/horaire`);
        return response.data;
    }

    async ecrireHoraireSalarie(employeId, horaireId) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/employes/${employeId}/horaire`, { horaire_id: horaireId });
        return response.data;
    }

    async ecrireHorairesTravail(horaires) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/horaires-travail`, horaires);
        return response.data;
    }

    // Offres des sociétés (contrat C20, Yros/Kavero#447), réservées au
    // superadmin. Route DÉDIÉE du proxy, en absolu : le préfixe /proxy/api mène
    // au tunnel vers Odoo, fermé sur `api/offres`. Aucune query string, le
    // proxy la refuse.
    async lireOffres() {
        const response = await this.axiosInstance.get(`${API_URL}/api/offres`);
        return response.data;
    }

    // Le corps est exactement `{company_id, offre_rh}` : le proxy refuse tout
    // autre corps (400), et un identifiant booléen.
    async changerOffre(companyId, offreRh) {
        const response = await this.axiosInstance.post(`${API_URL}/api/offres`, {
            company_id: companyId,
            offre_rh: offreRh,
        });
        return response.data;
    }

    // Droit « Correction des pointages » (contrat C27, YRH-CORE-013),
    // réservé aux rôles admin et superadmin. Routes DÉDIÉES du proxy, en
    // absolu : le préfixe /proxy/api mène au tunnel vers Odoo, fermé sur
    // `api/rh/droits-pointages`. Aucune query string au GET, le proxy la
    // refuse.
    async lireDroitsPointages() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/droits-pointages`);
        return response.data;
    }

    // Le corps est exactement `{user_id, corriger_pointages}` : le proxy refuse
    // tout autre corps (400), et un identifiant booléen. Les deux clés sont
    // reprises une à une, pour qu'un compte lu dans la liste n'envoie pas ses
    // autres champs.
    async ecrireDroitPointage({ user_id: userId, corriger_pointages: corrigerPointages }) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/droits-pointages`, {
            user_id: userId,
            corriger_pointages: corrigerPointages,
        });
        return response.data;
    }

    // Profil salarié enrichi (YRH-NEW-008, Yros/Kavero#479). Routes DÉDIÉES du
    // proxy (`_relayer_rh`), en absolu : le tunnel est fermé sur
    // `api/rh/mon-profil`. Le proxy déballe l'enveloppe JSON-RPC : la réponse se
    // lit à plat, et un refus d'Odoo arrive en 409 avec son enveloppe. Aucune
    // query string au GET (400).
    async lireMonProfil() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/mon-profil`);
        return response.data;
    }

    // Annuaire interne (YRH-NEW-009, Yros/Kavero#199). Routes DÉDIÉES du proxy,
    // en absolu : le tunnel est fermé sur `api/rh/annuaire`. `requete` est la
    // query string déjà construite (`nom`, `departement_id`,
    // `presents_uniquement`, `offset`, `limit`) ; un refus d'Odoo arrive en 409.
    async lireAnnuaire(requete) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/annuaire?${requete}`);
        return response.data;
    }

    async lireFicheAnnuaire(employeId) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/annuaire/${employeId}`);
        return response.data;
    }

    // Catalogue des compétences et des langues (YRH-NEW-008-T5,
    // Yros/Kavero#481). Route DÉDIÉE du proxy, en absolu, sans query string
    // (400) : les langues (liste fermée), les compétences connues et leurs niveaux.
    async lireCatalogueCompetences() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/catalogue-competences`);
        return response.data;
    }

    // Seules les quatre rubriques du contrat partent, reprises une à une : le
    // proxy refuse toute autre clé (400), `employe_id` compris — la fiche est
    // celle de la session.
    async ecrireMonProfil(rubriques) {
        const corps = {};
        ['bio', 'photo', 'competences', 'langues'].forEach((cle) => {
            if (cle in rubriques) corps[cle] = rubriques[cle];
        });
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/mon-profil`, corps);
        return response.data;
    }

    // Chaîne de validation (Yros/Kavero#468). Routes DÉDIÉES du proxy, en
    // absolu : le tunnel est fermé sur ces chemins. Le réglage est réservé aux
    // rôles RH ; les demandes de l'équipe et la décision s'ouvrent à tout rôle
    // connu, Odoo bornant à l'équipe du manager.
    async lireCircuitsValidation() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/circuits-validation`);
        return response.data;
    }

    async reglerCircuitValidation(type, circuit) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/circuits-validation`, { type, circuit });
        return response.data;
    }

    async lireJournalCircuits() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/circuits-validation/journal`);
        return response.data;
    }

    async lireDemandesEquipe() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/equipe/demandes`);
        return response.data;
    }

    async deciderDemandeEquipe({ type, id, approuver, motif }) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/equipe/demandes/decision`, { type, id, approuver, motif });
        return response.data;
    }

    // Onglet « Équipe » de la fiche collaborateur (contrat C31, Yros/Kavero#9),
    // réservé aux rôles RH. Route DÉDIÉE du proxy, en absolu : le tunnel est
    // fermé sur `api/rh/organigramme/equipe`.
    async lireEquipeCollaborateur(employeId) {
        const response = await this.axiosInstance.get(
            `${API_URL}/api/rh/organigramme/equipe?employe_id=${encodeURIComponent(employeId)}`);
        return response.data;
    }

    // Responsable d'un département (contrat C28, Yros/Kavero#469), réservé
    // aux rôles rh, admin et superadmin. Routes DÉDIÉES du proxy, en absolu :
    // le tunnel est fermé sur `api/rh/organigramme`. Le corps est exactement
    // `{departement_id, responsable_id}`, `responsable_id: null` pour retirer.
    async lireOrganigrammeDepartements() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/organigramme/departements`);
        return response.data;
    }

    async prevoirResponsableDepartement({ departement_id: departementId, responsable_id: responsableId }) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/organigramme/departements/apercu`, {
            departement_id: departementId,
            responsable_id: responsableId,
        });
        return response.data;
    }

    async designerResponsableDepartement({ departement_id: departementId, responsable_id: responsableId }) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/organigramme/departements/responsable`, {
            departement_id: departementId,
            responsable_id: responsableId,
        });
        return response.data;
    }

    // Action groupée sur une sélection (contrat C29, Yros/Kavero#469). Corps
    // exact `{employe_ids, responsable_id, departement_id}`, plus `remplacer`
    // pour l'écriture ; les clés sont reprises une à une.
    async prevoirAffectationOrganigramme({ employe_ids: employeIds, responsable_id: responsableId, departement_id: departementId }) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/organigramme/affectation/apercu`, {
            employe_ids: employeIds,
            responsable_id: responsableId,
            departement_id: departementId,
        });
        return response.data;
    }

    async appliquerAffectationOrganigramme({ employe_ids: employeIds, responsable_id: responsableId, departement_id: departementId, remplacer }) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/organigramme/affectation`, {
            employe_ids: employeIds,
            responsable_id: responsableId,
            departement_id: departementId,
            remplacer,
        });
        return response.data;
    }

    // Indicateur de l'organigramme (contrat C30, Yros/Kavero#469). Aucun
    // paramètre de requête : le proxy le refuse.
    // Compte Portail d'un salarié existant (contrat C33, Yros/Kavero#470),
    // réservé à la RH. `email` null garde l'adresse de la fiche.
    async lireComptePortail(employeId) {
        const response = await this.axiosInstance.get(
            `${API_URL}/api/rh/employes/${encodeURIComponent(employeId)}/compte-portail`);
        return response.data;
    }

    // Profil enrichi d'un salarié, vu par la RH (YRH-NEW-008-T6, Yros/Kavero#482).
    // Route DÉDIÉE du proxy, en absolu, sans query string (400), réservée aux
    // rôles RH ; le refus d'Odoo (salarié inconnu, autre société) arrive en 409.
    async lireProfilEmploye(employeId) {
        const response = await this.axiosInstance.get(
            `${API_URL}/api/rh/employes/${encodeURIComponent(employeId)}/profil`);
        return response.data;
    }

    // Modèle de duplication d'un employé (YRH-SHARED-010-T2, Yros/Kavero#195) :
    // manager, département, poste et compte Portail de l'employé à copier.
    // Route DÉDIÉE du proxy (Yros/Kavero#273), en absolu, sans query string (400),
    // réservée aux rôles RH ; le refus d'Odoo (employé inconnu, autre société,
    // rôle) arrive en 409 avec son enveloppe.
    async lireModeleDuplication(employeId) {
        const response = await this.axiosInstance.get(
            `${API_URL}/api/rh/employes/${encodeURIComponent(employeId)}/modele-duplication`);
        return response.data;
    }

    async inviterComptePortail(employeId, email) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/employes/${encodeURIComponent(employeId)}/compte-portail/inviter`, { email });
        return response.data;
    }

    async renvoyerInvitationPortail(employeId) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/employes/${encodeURIComponent(employeId)}/compte-portail/renvoyer`, {});
        return response.data;
    }

    // Départ d'un salarié (contrat C34), son solde de tout compte et les
    // domiciliations de salaire (contrat C35), Yros/Kavero#62. Routes DÉDIÉES du
    // proxy (Yros/Kavero#255) : le tunnel leur est fermé, tout part en absolu.
    async listerDeparts() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/departs`);
        return response.data;
    }

    async motifsDepart() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/departs/motifs`);
        return response.data;
    }

    async lireDepart(departId) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/departs/${encodeURIComponent(departId)}`);
        return response.data;
    }

    async creerDepart(depart) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/departs`, depart);
        return response.data;
    }

    async modifierDepart(departId, valeurs) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/modifier`, valeurs);
        return response.data;
    }

    // `action` : "lancer", "valider" ou "annuler".
    async faireAvancerDepart(departId, action) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/${action}`, {});
        return response.data;
    }

    async ajouterLigneChecklist(departId, libelle) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/checklist`, { libelle });
        return response.data;
    }

    async cocherLigneChecklist(departId, ligneId, fait) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/checklist/cocher`, { ligne_id: ligneId, fait });
        return response.data;
    }

    async lireStc(departId) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/stc`);
        return response.data;
    }

    async corrigerMontantsStc(departId, montants) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/stc/montants`, { montants });
        return response.data;
    }

    async revenirAuCalculStc(departId, champ) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/stc/revenir`, { champ });
        return response.data;
    }

    async recalculerStc(departId) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/stc/recalculer`, {});
        return response.data;
    }

    // `document` : "certificat" (certificat de travail) ou "recu" (reçu STC).
    async telechargerDocumentDepart(depart, document) {
        const { chemin, prefixe } = DOCUMENTS_DEPART[document];
        const response = await this.axiosInstance.get(
            `${API_URL}/api/rh/departs/${encodeURIComponent(depart.id)}/${chemin}`, { responseType: "blob" });
        this.downloadBlob(
            new Blob([response.data], { type: "application/pdf" }),
            `${prefixe}-${suffixeFichier(depart.employe?.nom, depart.id)}.pdf`
        );
    }

    async prevenirBanque(departId, domiciliationId) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/departs/${encodeURIComponent(departId)}/domiciliations/${encodeURIComponent(domiciliationId)}/prevenir`, {});
        return response.data;
    }

    async listerDomiciliations(employeId) {
        const response = await this.axiosInstance.get(
            `${API_URL}/api/rh/employes/${encodeURIComponent(employeId)}/domiciliations`);
        return response.data;
    }

    async creerDomiciliation(employeId, domiciliation) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/employes/${encodeURIComponent(employeId)}/domiciliations`, domiciliation);
        return response.data;
    }

    async modifierDomiciliation(domiciliationId, valeurs) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/domiciliations/${encodeURIComponent(domiciliationId)}/modifier`, valeurs);
        return response.data;
    }

    async leverDomiciliation(domiciliationId) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/domiciliations/${encodeURIComponent(domiciliationId)}/lever`, {});
        return response.data;
    }

    async telechargerPieceDomiciliation(domiciliation) {
        const response = await this.axiosInstance.get(
            `${API_URL}/api/rh/domiciliations/${encodeURIComponent(domiciliation.id)}/piece`, { responseType: "blob" });
        this.downloadBlob(new Blob([response.data]), domiciliation.piece || "domiciliation");
    }

    // Registre des dossiers disciplinaires (C39, Yros/Kavero#15). Route dédiée du
    // proxy, en absolu (jamais le tunnel). Le proxy valide les paramètres en
    // égalité stricte : une clé en plus ou un filtre vide donne 400.
    async listerDossiersDisciplinaires(filtres = {}) {
        const params = {};
        ['employe_id', 'etat', 'type'].forEach((cle) => {
            const valeur = filtres?.[cle];
            if (valeur !== undefined && valeur !== null && valeur !== '') params[cle] = valeur;
        });
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/disciplinaire/dossiers`, { params });
        return response.data;
    }

    // Écritures du registre (C39, bolt 171b) : en absolu, jamais par le tunnel.
    // Le proxy valide les corps en égalité stricte des clés : ils sont construits
    // ici, d'arguments positionnels, pour qu'aucune clé ne puisse fuir.
    async creerDossierDisciplinaire(employeId, dateFaits, faits) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/disciplinaire/dossiers/creer`,
            { employe_id: employeId, date_faits: dateFaits, faits });
        return response.data;
    }

    async cloreDossierDisciplinaire(dossierId) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/disciplinaire/dossiers/clore`,
            { dossier_id: dossierId });
        return response.data;
    }

    async annulerDossierDisciplinaire(dossierId, motif) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/disciplinaire/dossiers/annuler`,
            { dossier_id: dossierId, motif });
        return response.data;
    }

    // Étapes de convocation (C39, bolt 171c) : mêmes règles, corps positionnels.
    async ajouterConvocationDisciplinaire(dossierId, echeance, heure, lieu) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/disciplinaire/etapes/ajouter`,
            { dossier_id: dossierId, type: 'convocation', echeance, lieu, heure });
        return response.data;
    }

    async emettreEtapeDisciplinaire(etapeId) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/disciplinaire/etapes/emettre`,
            { etape_id: etapeId });
        return response.data;
    }

    async annulerEtapeDisciplinaire(etapeId, motif) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/disciplinaire/etapes/annuler`,
            { etape_id: etapeId, motif });
        return response.data;
    }

    async remettreEtapeDisciplinaire(etapeId, dateRemise) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/disciplinaire/etapes/remettre`,
            { etape_id: etapeId, date_remise: dateRemise });
        return response.data;
    }

    async deposerCopieSigneeDisciplinaire(etapeId, fichier) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/disciplinaire/etapes/document`,
            { etape_id: etapeId, fichier: String(fichier).replace(/^data:[^,]*,/, '') });
        return response.data;
    }

    // Un 200 n'est jamais lu (c'est le fichier). Un 404 arrive en Blob et n'est ni 403
    // ni 401 (aucune redirection) : on relit son enveloppe ici, `enveloppeDe` ne lit que le 409.
    // Le nom est construit côté portail (Content-Disposition n'est pas exposé par le CORS).
    async telechargerEtapeDisciplinaire(etape, numero, format) {
        try {
            const response = await this.axiosInstance.get(`${API_URL}/api/rh/disciplinaire/etapes/telecharger`,
                { params: { etape_id: etape.id, format }, responseType: 'blob' });
            this.downloadBlob(
                new Blob([response.data], { type: format === 'signe' ? TYPES_FORMAT_DOCUMENT.pdf : TYPES_FORMAT_DOCUMENT[format] }),
                `convocation-${String(numero).replace(/\//g, "-")}${format === 'signe' ? '-signee.pdf' : '.' + format}`
            );
            return { ok: true };
        } catch (error) {
            if (error?.response?.status !== 404) throw error;
            try {
                return JSON.parse(await lireTexte(error.response.data));
            } catch {
                return {};
            }
        }
    }

    // Yros/Kavero#494 : une ligne par salarié, l'écran assemble l'arbre.
    async lireArbreOrganigramme() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/organigramme/arbre`);
        return response.data;
    }

    async lireIndicateurOrganigramme() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/organigramme/indicateur`);
        return response.data;
    }

    async importPayrollCsv(file) {
        const formData = new FormData();
        // Le nom du champ est le contrat de la route proxy (Yros/Kavero#287).
        formData.append("file", file);

        // Content-Type retiré des en-têtes par défaut : axios doit le poser
        // lui-même pour y inclure le boundary multipart, sinon le proxy ne
        // sait pas découper le corps.
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/payroll/import-csv`,
            formData,
            { headers: { "Content-Type": undefined } }
        );
        return response.data;
    }

    async getPayrollLines(period) {
        const response = await this.axiosInstance.get(
            `${API_URL}/api/rh/payroll/lines`,
            { params: period ? { period } : {} }
        );
        return response.data?.lines || [];
    }

    async syncPayroll(period) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/payroll/sync`,
            { period }
        );
        return response.data;
    }

    // Présence du jour (contrat C12, Yros/Kavero#60). Route DÉDIÉE du proxy,
    // en absolu comme syncPayroll. Sans argument, sans query string : Odoo
    // calcule la fenêtre du jour dans le fuseau du demandeur, composer une
    // date ici rouvrirait le piège toISOString() de la review 029.
    async getPresenceDuJour() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/presence-du-jour`);
        return response.data;
    }

    // Demandes en attente comptées par type, pour la boîte « À traiter »
    // (Yros/Kavero#416). Route DÉDIÉE du proxy, en absolu comme
    // getPresenceDuJour, réservée aux rôles qui supervisent : l'appelant
    // vérifie la capacité avant, un refus renverrait vers la page 403.
    async compterATraiter() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/a-traiter`);
        return response.data;
    }

    // Cloche de notifications (Yros/Kavero#21). Routes DÉDIÉES du proxy, en
    // absolu comme compterATraiter, ouvertes à tout rôle connecté.
    async compterNotifications() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/notifications/compteur`);
        return response.data;
    }

    async listerNotifications() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/notifications`);
        return response.data;
    }

    // `corps` : `{ ids: [entiers] }` ou `{ toutes: true }`, rien d'autre.
    async marquerNotificationsLues(corps) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/notifications/lu`, corps);
        return response.data;
    }

    // Demandes RH (contrat C9). Routes DÉDIÉES du proxy, donc appelées en
    // absolu : le préfixe /proxy/api de axiosInstance mène au tunnel vers
    // Odoo, pas aux routes que le proxy sert lui-même.
    //
    // Avec pièces jointes (Yros/Kavero#431), le dépôt part en multipart : `type`,
    // `contenu` (objet JSON) et les fichiers sous `justificatifs`, la forme que le
    // proxy borne et relaie. Sans fichier, c'est le JSON de toujours. Le
    // Content-Type est retiré pour qu'axios pose lui-même le boundary.
    async creerDemandeRh(demande, fichiers = []) {
        if (!fichiers.length) {
            const response = await this.axiosInstance.post(`${API_URL}/api/rh/demandes`, demande);
            return response.data;
        }
        const formulaire = new FormData();
        formulaire.append('type', demande.type);
        formulaire.append('contenu', JSON.stringify(demande.contenu));
        fichiers.forEach((fichier) => formulaire.append('justificatifs', fichier));
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/demandes`,
            formulaire,
            { headers: { "Content-Type": undefined } }
        );
        return response.data;
    }

    // Yros/Kavero#492 : le titulaire retouche sa demande d'argent avant décision.
    async modifierDemandeRh(id, type, contenu) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/mes-demandes/${id}/modifier`, { type, contenu });
        return response.data;
    }

    async annulerDemandeRh(id, type) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/mes-demandes/${id}/annuler`, { type });
        return response.data;
    }

    async listerDemandesRh(perimetre) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/demandes`, {
            params: perimetre ? { perimetre } : undefined,
        });
        return response.data;
    }

    // Ordres de mission (Yros/Kavero#125). L'émission passe par la route
    // DÉDIÉE du proxy, donc en absolu ; la liste passe par le tunnel générique,
    // comme les bulletins, donc en relatif au préfixe /proxy/api.
    async emettreOrdreMission(ordre) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/ordres-mission`, ordre);
        return response.data;
    }

    async listerOrdresMission(perimetre) {
        const response = await this.axiosInstance.get('/rh/ordres-mission', {
            params: perimetre ? { perimetre } : undefined,
        });
        return response.data;
    }

    // Transition de statut d'un ordre de mission (Yros/Kavero#441, contrat
    // C21). Route DÉDIÉE du proxy, donc absolue : le tunnel refuse l'écriture
    // sur cette famille depuis Yros/Kavero#440.
    async changerStatutOrdreMission(ordreId, statut) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/ordres-mission/${ordreId}/statut`,
            { statut }
        );
        return response.data;
    }

    // Les deux documents d'un ordre de mission (Yros/Kavero#397, #425), en PDF
    // ou en Word (#465 ; le Word est réservé par Odoo au gestionnaire RH), par
    // le tunnel comme les bulletins. Le nom du fichier se construit ici depuis
    // le numéro : le proxy ne relaie pas le Content-Disposition, et les `/` du
    // numéro ne passent pas dans un nom de fichier.
    // `document` : "ordre" ou "engagement" ; `format` : "pdf" ou "docx".
    async telechargerDocumentOrdreMission(ordre, document, format) {
        const { chemin, prefixe } = DOCUMENTS_ORDRE_MISSION[document];
        const response = await this.getBlobFile(`/rh/ordres-mission/${ordre.id}${chemin}/${format}`);
        this.downloadBlob(
            new Blob([response.data], { type: TYPES_FORMAT_DOCUMENT[format] }),
            `${prefixe}-${String(ordre.numero).replace(/\//g, "-")}.${format}`
        );
    }

    // Badge d'un collaborateur (contrat C26, Yros/Kavero#222), par le tunnel
    // comme l'ordre de mission. Le nom du fichier se construit ici depuis le
    // nom du salarié, réduit à `[a-z0-9-]` : le proxy ne relaie pas le
    // Content-Disposition, et la fiche ne porte pas le matricule.
    async telechargerBadge(employe) {
        const response = await this.getBlobFile(`/rh/employes/${employe.id}/badge`);
        const suffixe = String(employe.name || "")
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
        this.downloadBlob(
            new Blob([response.data], { type: "application/pdf" }),
            `badge-${suffixe || employe.id}.pdf`
        );
    }

    // Géocodage (contrat C17). Route dédiée du proxy, donc absolue comme
    // `getLieuTravail`. Le service tiers n'est jamais appelé depuis ici : une
    // clé d'API dans ce bundle serait une clé publique. 503 quand la recherche
    // n'est pas configurée, 502 quand le service est en panne — deux situations
    // qui n'appellent pas la même phrase, l'appelant les distingue au statut.
    async geocoderAdresse(adresse) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/geocodage`, {
            params: { adresse },
        });
        return response.data;
    }

    async listerSoldeRh(employeId) {
        const url = employeId ? `${API_URL}/api/rh/solde?employe_id=${employeId}` : `${API_URL}/api/rh/solde`;
        const response = await this.axiosInstance.get(url);
        return response.data;
    }

    // Supervision RH des demandes (bolt 046, Yros/Kavero#71). Route DÉDIÉE du
    // proxy, patron `getPresenceDuJour` : en absolu, sans argument ni query
    // string — le proxy refuse tout `request.args` sur cette route (400).
    async listerSupervisionRh() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/demandes/supervision`);
        return response.data;
    }

    // Pointage (contrat C11). Route dédiée du proxy, donc absolue comme
    // `creerDemandeRh`. Le refus métier ressort en 409 : aucun intercepteur ne
    // le traite, l'appelant reçoit l'enveloppe intacte.
    async poserPointage(position) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/pointage`, position);
        return response.data;
    }

    // Sans argument : la semaine courante. Avec `du` et `au` (AAAA-MM-JJ, 62
    // jours au plus) : l'historique du salarié sur cette période (C14 étendu).
    async getMesPointages(du, au) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/mes-pointages`, {
            params: du && au ? { du, au } : undefined,
        });
        return response.data;
    }

    // Note de fin de journée d'un pointage passé (contrat C26, Yros/Kavero#464).
    // Route dédiée du proxy, donc absolue. Le corps a exactement deux clés ;
    // `note: null` efface. Le refus métier ressort en 409, enveloppe intacte.
    async ecrireNoteSortie({ pointage_id, note }) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/mes-pointages/note`, { pointage_id, note });
        return response.data;
    }

    // Pointages vus par la RH (contrat C23, Yros/Kavero#453). Routes dédiées du
    // proxy, en absolu. `filtres` : `du` et `au` obligatoires, `employe_id` et
    // `anomalie` facultatifs — le proxy type chaque paramètre et refuse tout
    // autre.
    async listerPointagesRh(filtres) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/pointages`, { params: filtres });
        return response.data;
    }

    // `pointage_id: null` ajoute un pointage, sinon corrige celui-là ; le motif
    // est obligatoire des deux côtés.
    async corrigerPointage(params) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/pointages/corriger`, params);
        return response.data;
    }

    // Feuille de temps du salarié connecté (contrat C22, Yros/Kavero#207).
    // Routes dédiées du proxy, donc absolues. `lundi` est facultatif : sans lui,
    // Odoo rend la semaine courante, dans le fuseau du salarié. Le proxy exige
    // les quatre clés du corps de la correction (`heures: null` l'annule).
    async lireFeuilleTemps(lundi) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/feuille-temps`, {
            params: lundi ? { lundi } : undefined,
        });
        return response.data;
    }

    async corrigerJourFeuilleTemps(params) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/feuille-temps/corriger`, params);
        return response.data;
    }

    async soumettreFeuilleTemps(lundi) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/feuille-temps/soumettre`, { lundi });
        return response.data;
    }

    // Feuilles de temps vues par la RH (contrat C22, Yros/Kavero#207). Routes
    // dédiées du proxy, en absolu. Le proxy type chaque paramètre et refuse
    // tout autre (400 sans appel) : seuls les filtres posés partent.
    async listerFeuillesTemps(filtres = {}) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/feuilles-temps`, {
            params: Object.keys(filtres).length ? filtres : undefined,
        });
        return response.data;
    }

    async lireFeuilleTempsRh(employeId, lundi) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/feuilles-temps/detail`, {
            params: { employe_id: employeId, lundi },
        });
        return response.data;
    }

    async renvoyerFeuilleTemps(params) {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/feuilles-temps/renvoyer`, params);
        return response.data;
    }

    async annulerDernierPointage() {
        const response = await this.axiosInstance.post(`${API_URL}/api/rh/annuler-pointage`, {});
        return response.data;
    }

    // Décision d'une demande (Yros/Kavero#261). Le type voyage avec l'appel :
    // le proxy y choisit le couple modèle/méthode dans une table fermée, et
    // sans lui une avance serait décidée comme un congé. Il reste facultatif
    // ici — l'écran de validation des congés appelle sans rien, et le proxy
    // retombe alors sur le congé.
    // `actionId` : l'action que la RH retient sur une demande « Autres »
    // (Yros/Kavero#433). Absent, le corps est celui d'avant.
    // `moisRemboursement` (AAAA-MM) n'existe que pour une note de frais
    // (Yros/Kavero#437) : absent, Odoo rembourse sur le mois de validation.
    async approuverDemandeRh(id, type, actionId, moisRemboursement) {
        const corps = type
            ? {
                type,
                ...(actionId ? { action_id: actionId } : {}),
                ...(moisRemboursement ? { mois_remboursement: moisRemboursement } : {}),
            }
            : undefined;
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/demandes/${id}/approuver`,
            corps
        );
        return response.data;
    }

    // Les actions que la RH peut retenir sur une demande « Autres »
    // (Yros/Kavero#433), celles de sa société. Route dédiée du proxy, en absolu.
    async listerActionsAutres() {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/autres-actions`);
        return response.data;
    }

    // Le motif n'est pas décoratif : c'est lui que le salarié lira dans son
    // espace. Le proxy le refuse vide, l'écran aussi.
    async refuserDemandeRh(id, type, motif) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/demandes/${id}/refuser`,
            { type, motif }
        );
        return response.data;
    }

    // Note de frais (Yros/Kavero#18). Routes DÉDIÉES du proxy, donc en absolu.
    // Content-Type retiré des en-têtes par défaut, comme pour l'import CSV :
    // axios doit le poser lui-même pour y inclure le boundary multipart.
    async deposerNoteFrais(formulaire) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/notes-frais`,
            formulaire,
            { headers: { "Content-Type": undefined } }
        );
        return response.data;
    }

    // Remise en main propre (Yros/Kavero#122) : la RH confirme avoir donné
    // le document en main propre au salarié, une fois la demande approuvée
    // et le PDF imprimé. Même patron qu'annulerDernierPointage : POST sans
    // corps vers une route dédiée.
    async marquerAttestationRemise(demandeId) {
        const response = await this.axiosInstance.post(
            `${API_URL}/api/rh/demandes/${demandeId}/attestation-remise`,
            {}
        );
        return response.data;
    }

    // Un justificatif, désigné par sa pièce, revient en blob : c'est l'écran
    // qui décide de l'ouvrir.
    // Yros/Kavero#489 : le contrat signé, déposé par la RH sur le contrat.
    async lireMonContratSigne() {
        const response = await this.axiosInstance.get('/rh/contrat/document', { responseType: "blob" });
        return response.data;
    }

    async lireContratSigne(contratId) {
        const response = await this.axiosInstance.get(`/contract/${contratId}/signed-document`, { responseType: "blob" });
        return response.data;
    }

    async deposerContratSigne(contratId, fichier) {
        const donnees = new FormData();
        donnees.append('file', fichier);
        const response = await this.axiosInstance.post(`/contract/${contratId}/signed-document`, donnees,
            { headers: { "Content-Type": "multipart/form-data" } });
        return response.data;
    }

    async retirerContratSigne(contratId) {
        const response = await this.axiosInstance.delete(`/contract/${contratId}/signed-document`);
        return response.data;
    }

    async lireJustificatif(id, type, piece) {
        const response = await this.axiosInstance.get(`${API_URL}/api/rh/justificatifs/${id}`, {
            params: { type, piece },
            responseType: "blob",
        });
        return response.data;
    }

    getApiErrorMessage(error, defaultMessage) {
        if (error.response?.status === 403) {
            return i18n.t('employeeDetails.insufficientRights');
        }
        return defaultMessage;
    }
}

export const apiService = new ApiService();
